/* MaterialsUsedPanel — iter97.20 full redesign.
 *
 * Card-based material log with cascading selectors, batch traceability,
 * substitution detection, and unplanned-consumable rows.
 *
 * Each material row is a Card (not a table row) showing:
 *  - Category icon + row label + badge (BOM / ADDITIONAL / SUBSTITUTED)
 *  - 3-level selector: Category dropdown → Material SKU → Variant
 *  - Batch/Lot selector (sourced from inventory_batches for the SKU)
 *  - Expected vs Actual qty + wastage with reason dropdown
 *  - Stock remaining indicator
 *  - Substitution warning banner + reason dropdown when chosen SKU
 *    differs from the BOM default
 *
 * "+ Add material" appends an unplanned/ADDITIONAL row with no expected_qty.
 *
 * Summary chip strip surfaces: severity counts, total wastage,
 * substitution count, additional count, batches-missing count.
 *
 * Submit → POST /api/production/cases/{id}/materials-log with new fields:
 *   chosen_material_id, variant_id, variant_label, batch_id, batch_lot,
 *   is_additional, substitution_reason.
 *
 * Once submitted, the entire panel locks (read-only) with a green banner.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
    CheckCircle, Warning, Stack, Lightning, ClipboardText, ArrowsCounterClockwise,
    PaperPlaneTilt, Lock, Plus, X, Drop, Package, Flask, GearSix,
    Tooth, Barcode, ArrowsLeftRight, LockOpen,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";


/* ───── Category model + cosmetic mappings ─────────────────────────────── */

const PRODUCTION_CATEGORIES = [
    { id: "tpu_sheet",       label: "Aligner sheet",     Icon: Stack,    tone: "violet" },
    { id: "retainer_sheet",  label: "Retainer sheet",    Icon: Tooth,    tone: "sky" },
    { id: "resin",           label: "Resin",             Icon: Drop,     tone: "amber" },
    { id: "ipa",             label: "IPA / Cleaning",    Icon: Flask,    tone: "emerald" },
    { id: "packaging",       label: "Packaging",         Icon: Package,  tone: "rose" },
    { id: "consumables",     label: "Consumable",        Icon: GearSix,  tone: "slate" },
    { id: "other",           label: "Other",             Icon: GearSix,  tone: "slate" },
];
const CAT_BY_ID = Object.fromEntries(PRODUCTION_CATEGORIES.map((c) => [c.id, c]));

const TONE_BG = {
    violet:  "bg-violet-100 text-violet-700",
    sky:     "bg-sky-100 text-sky-700",
    amber:   "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose:    "bg-rose-100 text-rose-700",
    slate:   "bg-slate-100 text-slate-700",
};

const WASTAGE_REASONS = [
    { value: "forming_defect_rerun",  label: "Forming defect re-run" },
    { value: "tear_during_trimming",  label: "Material tear during trimming" },
    { value: "wrong_sheet_loaded",    label: "Wrong sheet loaded" },
    { value: "print_failure",         label: "Print failure" },
    { value: "other",                 label: "Other" },
];
const WASTAGE_LABEL = Object.fromEntries(WASTAGE_REASONS.map((r) => [r.value, r.label]));

const SUBSTITUTION_REASONS = [
    { value: "out_of_stock",          label: "Specified material out of stock" },
    { value: "defective_batch",       label: "Defective batch — replaced" },
    { value: "admin_approved",        label: "Admin approved substitution" },
    { value: "tech_error_corrected",  label: "Technician error — corrected" },
    { value: "other",                 label: "Other (see note)" },
];
const SUBSTITUTION_LABEL = Object.fromEntries(SUBSTITUTION_REASONS.map((r) => [r.value, r.label]));

const PURPOSE_ROW_LABEL = {
    aligner_sheet_upper: "Upper aligner sheets",
    aligner_sheet_lower: "Lower aligner sheets",
    aligner_sheet:       "Aligner sheets",   // legacy fallback
    retainer_upper:      "Upper retainer sheet",
    retainer_lower:      "Lower retainer sheet",
    retainer:            "Retainer sheets",  // legacy fallback
    ipa:                 "IPA wash",
    resin:               "3D print resin",
    packaging:           "Packaging kit",
};


function severityOf(actual, expected) {
    const over = Number(actual || 0) - Number(expected || 0);
    if (over <= 0) return "normal";
    if (over <= 2) return "moderate";
    return "high";
}


/* ───── Main component ─────────────────────────────────────────────────── */

export default function MaterialsUsedPanel({ caseObj, canEdit, onChange }) {
    const caseId = caseObj?.id;
    const { user } = useAuth();
    const isAdmin = user?.role === "admin" || user?.staff_type === "coe_lead";
    const [bom, setBom] = useState(null);
    const [rows, setRows] = useState([]); // each row: see ROW_SHAPE below
    const [historicMovements, setHistoricMovements] = useState([]);
    const [allMaterials, setAllMaterials] = useState([]); // cached list for cascading dropdowns
    const [batchesByMaterial, setBatchesByMaterial] = useState({}); // material_id -> list of batches
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [technicianNote, setTechnicianNote] = useState("");
    const [unlockOpen, setUnlockOpen] = useState(false);

    /* Already logged? Backend stamps production_materials_logged_at on the case. */
    const alreadyLogged = !!caseObj?.production_materials_logged_at;
    const readOnly = !canEdit || alreadyLogged;

    /* ROW_SHAPE — every row carries:
       - kind: "bom" | "additional"
       - bom_purpose, arch, row_label (from BOM line)
       - material_id: BOM-default material (used as substitution baseline)
       - chosen_material_id: what the tech actually picked (defaults to material_id)
       - variant_id / variant_label
       - batch_id / batch_lot
       - expected_qty, actual_qty
       - wastage_reason, substitution_reason, note */

    const fetchBatchesFor = async (materialId) => {
        if (!materialId || batchesByMaterial[materialId] != null) return;
        try {
            const r = await api.get(`/admin/inventory/batches?material_id=${materialId}&status=active`);
            setBatchesByMaterial((p) => ({ ...p, [materialId]: r.data || [] }));
        } catch {
            setBatchesByMaterial((p) => ({ ...p, [materialId]: [] }));
        }
    };

    const reload = async () => {
        if (!caseId) return;
        setLoading(true);
        try {
            const [bomRes, histRes, matsRes] = await Promise.all([
                api.get(`/cases/${caseId}/expected-bom`),
                api.get(`/admin/inventory/cases/${caseId}/materials-used`).catch(() => ({ data: [] })),
                api.get(`/admin/inventory/materials`).catch(() => ({ data: [] })),
            ]);
            setBom(bomRes.data);
            setAllMaterials(matsRes.data || []);
            const lines = bomRes.data?.lines || [];
            const histRows = Array.isArray(histRes.data) ? histRes.data : (histRes.data?.rows || []);

            if (alreadyLogged) {
                // Reconstruct rows from historic movements
                const byKey = new Map();
                for (const m of histRows) {
                    if (m.movement_type !== "production_usage") continue;
                    // Group by (bom_purpose|arch + material_id) so additional rows don't overwrite BOM rows
                    const key = `${m.bom_purpose || "extra"}|${m.is_additional ? "x" : "b"}|${m.material_id}`;
                    const prev = byKey.get(key);
                    if (!prev || (m.created_at || "") > (prev.created_at || "")) {
                        byKey.set(key, m);
                    }
                }
                // First produce one row per BOM line (matched with its movement),
                // then append any movements that are additional/unplanned.
                const bomRows = lines.map((l) => {
                    const mv = [...byKey.values()].find((m) =>
                        m.bom_purpose === l.bom_purpose && !m.is_additional,
                    );
                    return {
                        kind: "bom",
                        bom_purpose: l.bom_purpose,
                        arch: l.arch,
                        row_label: l.row_label || PURPOSE_ROW_LABEL[l.bom_purpose] || l.bom_purpose,
                        material_id: l.material_id,
                        material_default_sku: l.material_sku,
                        material_default_name: l.material_name,
                        category: categoryFromPurpose(l.bom_purpose),
                        chosen_material_id: mv?.material_id || l.material_id,
                        variant_id: mv?.variant_id || "",
                        variant_label: mv?.variant_label || "",
                        batch_id: mv?.batch_id || "",
                        batch_lot: mv?.batch_lot || "",
                        expected_qty: l.expected_qty,
                        actual_qty: mv?.actual_qty != null ? mv.actual_qty : (mv?.delta != null ? Math.abs(mv.delta || 0) : null),
                        wastage_qty: mv?.wastage_qty || 0,
                        wastage_reason: mv?.wastage_reason || "",
                        wastage_severity: mv?.wastage_severity,
                        substitution_reason: mv?.substitution_reason || "",
                        is_substitution: !!mv?.is_substitution,
                        note: mv?.reason && !mv.reason.startsWith("Production usage") ? mv.reason : "",
                        current_stock: l.current_stock,
                        reorder_threshold: l.reorder_threshold,
                        unit: l.unit,
                    };
                });
                const additionalRows = [...byKey.values()]
                    .filter((m) => m.is_additional)
                    .map((m) => ({
                        kind: "additional",
                        bom_purpose: null,
                        arch: null,
                        row_label: "Unplanned consumable",
                        material_id: m.material_id,
                        chosen_material_id: m.material_id,
                        category: m.bom_purpose || "consumables",
                        variant_id: m.variant_id || "",
                        variant_label: m.variant_label || "",
                        batch_id: m.batch_id || "",
                        batch_lot: m.batch_lot || "",
                        expected_qty: 0,
                        actual_qty: m.actual_qty || Math.abs(m.delta || 0),
                        wastage_qty: 0,
                        wastage_reason: "",
                        substitution_reason: "",
                        is_substitution: false,
                        note: m.reason && !m.reason.startsWith("Production usage") ? m.reason : "",
                        current_stock: null,
                        unit: null,
                    }));
                setRows([...bomRows, ...additionalRows]);
            } else {
                // Seed BOM rows with actual=expected; chosen=BOM-default; empty everything else
                setRows(lines.map((l) => ({
                    kind: "bom",
                    bom_purpose: l.bom_purpose,
                    arch: l.arch,
                    row_label: l.row_label || PURPOSE_ROW_LABEL[l.bom_purpose] || l.bom_purpose,
                    material_id: l.material_id,
                    material_default_sku: l.material_sku,
                    material_default_name: l.material_name,
                    category: categoryFromPurpose(l.bom_purpose),
                    chosen_material_id: l.material_id,
                    variant_id: "",
                    variant_label: "",
                    batch_id: "",
                    batch_lot: "",
                    expected_qty: l.expected_qty,
                    actual_qty: l.expected_qty,
                    wastage_qty: 0,
                    wastage_reason: "",
                    substitution_reason: "",
                    is_substitution: false,
                    note: "",
                    current_stock: l.current_stock,
                    reorder_threshold: l.reorder_threshold,
                    unit: l.unit,
                })));
            }
            setHistoricMovements(histRows);
            setTechnicianNote(caseObj?.production_materials_technician_note || "");

            // Pre-load batches for every default material on screen
            const matIds = new Set();
            (lines || []).forEach((l) => l.material_id && matIds.add(l.material_id));
            histRows.forEach((m) => m.material_id && matIds.add(m.material_id));
            for (const mid of matIds) await fetchBatchesFor(mid);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load Expected BOM");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseId, alreadyLogged]);

    /* ─── Row mutation helpers ────────────────────────────────────────── */

    const update = (idx, patch) => setRows((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

    const setActual = (idx, val) => {
        update(idx, {
            actual_qty: val === "" ? "" : Number(val),
            // Wastage recomputed in the JSX from row.actual + row.expected to avoid drift
        });
    };

    const setCategory = (idx, cat) => {
        const row = rows[idx];
        if (row.kind !== "additional") return;  // BOM rows: category is fixed
        update(idx, {
            category: cat,
            chosen_material_id: "",
            variant_id: "",
            variant_label: "",
            batch_id: "",
            batch_lot: "",
            current_stock: null,
            unit: null,
        });
    };

    const setChosenMaterial = (idx, mid) => {
        const mat = allMaterials.find((m) => m.id === mid);
        update(idx, {
            chosen_material_id: mid,
            variant_id: "",
            variant_label: "",
            batch_id: "",
            batch_lot: "",
            current_stock: mat?.current_stock ?? null,
            unit: mat?.unit ?? null,
        });
        fetchBatchesFor(mid);
    };

    const setVariant = (idx, vid) => {
        const row = rows[idx];
        const mat = allMaterials.find((m) => m.id === row.chosen_material_id);
        const variant = (mat?.variants || []).find((v) => v.id === vid);
        update(idx, {
            variant_id: vid,
            variant_label: variant?.label || "",
        });
    };

    const setBatch = (idx, bid) => {
        const row = rows[idx];
        const batch = (batchesByMaterial[row.chosen_material_id] || []).find((b) => b.id === bid);
        update(idx, {
            batch_id: bid,
            batch_lot: batch?.lot_number || "",
        });
    };

    const setBatchLotFreeText = (idx, lot) => {
        update(idx, { batch_id: "", batch_lot: lot });
    };

    const addAdditionalRow = () => {
        setRows((p) => [
            ...p,
            {
                kind: "additional",
                bom_purpose: null,
                arch: null,
                row_label: "Unplanned consumable",
                material_id: null,
                chosen_material_id: "",
                category: "consumables",
                variant_id: "",
                variant_label: "",
                batch_id: "",
                batch_lot: "",
                expected_qty: 0,
                actual_qty: "",
                wastage_qty: 0,
                wastage_reason: "",
                substitution_reason: "",
                is_substitution: false,
                note: "",
                current_stock: null,
                unit: null,
            },
        ]);
    };

    const removeRow = (idx) => {
        const row = rows[idx];
        if (row.kind !== "additional") return; // only additional rows can be removed
        setRows((p) => p.filter((_, i) => i !== idx));
    };

    /* ─── Substitution detection + materials filtered by category ────── */

    const materialsByCategory = useMemo(() => {
        const out = {};
        for (const m of allMaterials) {
            const c = m.category || "other";
            if (!out[c]) out[c] = [];
            out[c].push(m);
        }
        return out;
    }, [allMaterials]);

    /* ─── Validation ──────────────────────────────────────────────────── */

    const validation = useMemo(() => {
        const errors = [];
        for (const r of rows) {
            if (!r.chosen_material_id) {
                errors.push(`Pick a material for "${r.row_label}"`);
                continue;
            }
            if (r.actual_qty === "" || r.actual_qty === null || r.actual_qty === undefined) {
                errors.push(`Actual qty missing for "${r.row_label}"`);
                continue;
            }
            const chosenMat = allMaterials.find((m) => m.id === r.chosen_material_id);
            if (chosenMat && Number(r.actual_qty) > Number(chosenMat.current_stock || 0)) {
                errors.push(`${chosenMat.sku}: need ${r.actual_qty}, only ${chosenMat.current_stock} ${chosenMat.unit} in stock`);
                continue;
            }
            const wastage = Math.max(0, Number(r.actual_qty || 0) - Number(r.expected_qty || 0));
            if (r.kind === "bom" && wastage > 0 && !r.wastage_reason) {
                errors.push(`${r.row_label}: wastage reason required (${wastage} ${r.unit} over)`);
            }
            const isSub = r.kind === "bom" && r.chosen_material_id !== r.material_id;
            if (isSub && !r.substitution_reason) {
                errors.push(`${r.row_label}: substitution reason required`);
            }
        }
        return errors;
    }, [rows, allMaterials]);

    /* ─── Summary counters ────────────────────────────────────────────── */

    const summary = useMemo(() => {
        const s = { normal: 0, moderate: 0, high: 0, wastageUnits: 0, substitutions: 0, additional: 0, batchesMissing: 0 };
        for (const r of rows) {
            if (r.kind === "bom") {
                const sev = severityOf(r.actual_qty, r.expected_qty);
                s[sev] = (s[sev] || 0) + 1;
                s.wastageUnits += Math.max(0, Number(r.actual_qty || 0) - Number(r.expected_qty || 0));
                if (r.chosen_material_id !== r.material_id) s.substitutions += 1;
            } else if (r.kind === "additional") {
                s.additional += 1;
            }
            if (Number(r.actual_qty || 0) > 0 && !r.batch_lot && !r.batch_id) s.batchesMissing += 1;
        }
        return s;
    }, [rows]);

    /* ─── Submit ──────────────────────────────────────────────────────── */

    const submit = async () => {
        if (validation.length > 0 || saving) return;
        setSaving(true);
        try {
            const payload = {
                lines: rows
                    .filter((r) => Number(r.actual_qty || 0) >= 0 && r.chosen_material_id)
                    .map((r) => ({
                        material_id: r.material_id || r.chosen_material_id,
                        chosen_material_id: r.chosen_material_id,
                        expected_qty: Number(r.expected_qty || 0),
                        actual_qty: Number(r.actual_qty || 0),
                        bom_purpose: r.bom_purpose || null,
                        arch: r.arch || null,
                        variant_id: r.variant_id || null,
                        variant_label: r.variant_label || null,
                        batch_id: r.batch_id || null,
                        batch_lot: r.batch_lot || null,
                        is_additional: r.kind === "additional",
                        wastage_reason: r.wastage_reason || null,
                        substitution_reason: r.chosen_material_id !== r.material_id && r.kind === "bom" ? (r.substitution_reason || null) : null,
                        note: r.note || "",
                    })),
                technician_note: technicianNote || "",
            };
            const r = await api.post(`/production/cases/${caseId}/materials-log`, payload);
            const subs = r.data?.substitutions?.length || 0;
            const high = r.data?.high_wastage?.length || 0;
            const low = r.data?.low_stock_alerts?.length || 0;
            const add = r.data?.additional_count || 0;
            const parts = [`${payload.lines.length} line${payload.lines.length === 1 ? "" : "s"} logged`];
            if (subs) parts.push(`${subs} substitution${subs === 1 ? "" : "s"} → admin alerted`);
            if (high) parts.push(`${high} high wastage`);
            if (low) parts.push(`${low} low-stock`);
            if (add) parts.push(`${add} additional`);
            toast.success(parts.join(" · "));
            onChange?.();
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to log materials");
        } finally {
            setSaving(false);
        }
    };

    if (!caseId) return null;

    /* ─── Render ──────────────────────────────────────────────────────── */

    return (
        <div data-testid="materials-used-panel" className="rounded-2xl border border-border bg-card overflow-hidden">
            {/* Header */}
            <PanelHeader alreadyLogged={alreadyLogged} caseObj={caseObj} />

            {loading && (
                <div className="px-5 py-6 text-center text-xs text-muted-foreground">Loading expected BOM…</div>
            )}

            {!loading && rows.length === 0 && (
                <div className="px-5 py-6 text-center" data-testid="materials-empty">
                    <ClipboardText size={28} weight="duotone" className="mx-auto text-muted-foreground mb-2" />
                    <div className="text-sm font-semibold">No BOM available</div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                        Manufacturing spec or plan counts aren&apos;t set. Ask admin to complete the spec first.
                    </div>
                </div>
            )}

            {!loading && rows.length > 0 && (
                <>
                    {/* Summary chips */}
                    <SummaryStrip summary={summary} alreadyLogged={alreadyLogged} />

                    {/* Card stack */}
                    <div className="p-4 space-y-3 bg-secondary/20" data-testid="materials-card-stack">
                        {rows.map((r, idx) => (
                            <MaterialRowCard
                                key={`${r.kind}-${r.bom_purpose || "x"}-${idx}`}
                                row={r}
                                idx={idx}
                                readOnly={readOnly}
                                allMaterials={allMaterials}
                                materialsByCategory={materialsByCategory}
                                batches={batchesByMaterial[r.chosen_material_id] || []}
                                onUpdate={(patch) => update(idx, patch)}
                                onSetCategory={(c) => setCategory(idx, c)}
                                onSetMaterial={(mid) => setChosenMaterial(idx, mid)}
                                onSetVariant={(vid) => setVariant(idx, vid)}
                                onSetBatch={(bid) => setBatch(idx, bid)}
                                onSetBatchLotFreeText={(lot) => setBatchLotFreeText(idx, lot)}
                                onSetActual={(v) => setActual(idx, v)}
                                onRemove={() => removeRow(idx)}
                            />
                        ))}

                        {!readOnly && (
                            <button
                                type="button"
                                onClick={addAdditionalRow}
                                className="w-full px-4 py-3 rounded-xl border border-dashed border-primary/40 text-primary hover:bg-primary/5 transition-colors inline-flex items-center justify-center gap-1.5 font-semibold text-sm"
                                data-testid="materials-add-additional"
                            >
                                <Plus size={14} weight="bold" /> Add unplanned consumable
                            </button>
                        )}

                        {/* iter97.21 — Bottom summary bar. Mirrors the top
                            chip strip but lives below the card stack so a
                            tech who's just finished filling in the last row
                            can confirm the whole log at a glance before
                            hitting Submit. Hidden once logged (the locked
                            banner takes over). */}
                        {!alreadyLogged && (
                            <BottomSummaryBar summary={summary} data-testid="materials-bottom-summary" />
                        )}
                    </div>

                    {/* Locked banner OR submit footer */}
                    {alreadyLogged ? (
                        <div
                            className="px-5 py-3 border-t border-border bg-emerald-50/60 flex items-start justify-between gap-3 flex-wrap"
                            data-testid="materials-locked-banner"
                        >
                            <div className="flex items-center gap-2 text-xs text-emerald-800">
                                <CheckCircle size={14} weight="fill" className="text-emerald-700 shrink-0" />
                                <span>
                                    Materials logged on{" "}
                                    <span className="font-semibold">
                                        {new Date(caseObj.production_materials_logged_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                    {" "}by{" "}
                                    <span className="font-semibold">{caseObj.production_materials_logged_by_name || "tech"}</span>.
                                    {isAdmin ? " You can unlock for corrections." : " Admin can unlock for corrections."}
                                </span>
                            </div>
                            {isAdmin && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setUnlockOpen(true)}
                                    className="rounded-full text-amber-700 border-amber-300 hover:bg-amber-50"
                                    data-testid="materials-unlock-btn"
                                >
                                    <LockOpen size={12} className="mr-1.5" weight="duotone" /> Unlock for editing
                                </Button>
                            )}
                        </div>
                    ) : canEdit ? (
                        <div className="px-5 py-3 border-t border-border space-y-3">
                            <div>
                                <label className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                                    Technician note (optional)
                                </label>
                                <Input
                                    value={technicianNote}
                                    onChange={(e) => setTechnicianNote(e.target.value)}
                                    placeholder="e.g. Re-ran stage 4 due to bubble defect on first tray"
                                    className="mt-1 h-9 text-sm"
                                    data-testid="materials-tech-note"
                                />
                            </div>
                            {validation.length > 0 && (
                                <div className="text-[11px] text-rose-700 bg-rose-50/60 border border-rose-200 rounded-lg px-3 py-2" data-testid="materials-validation">
                                    <div className="font-semibold mb-0.5">Fix before submit:</div>
                                    <ul className="list-disc list-inside leading-tight">
                                        {validation.slice(0, 4).map((m, i) => <li key={i}>{m}</li>)}
                                    </ul>
                                </div>
                            )}
                            <div className="flex items-center gap-2 justify-end">
                                <Button variant="outline" size="sm" onClick={reload} disabled={saving} data-testid="materials-reset">
                                    <ArrowsCounterClockwise size={13} weight="bold" className="mr-1" /> Reset to BOM
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={submit}
                                    disabled={saving || validation.length > 0}
                                    className="bg-primary text-white"
                                    data-testid="materials-submit"
                                >
                                    <PaperPlaneTilt size={13} weight="fill" className="mr-1" />
                                    {saving ? "Logging…" : "Log materials & unlock QC"}
                                </Button>
                            </div>
                        </div>
                    ) : null}
                </>
            )}

            {/* iter97.21 — Admin unlock dialog. Mounts only when the user
                is admin / coe_lead AND the case is locked. */}
            {isAdmin && (
                <UnlockMaterialsDialog
                    open={unlockOpen}
                    caseId={caseId}
                    caseCode={caseObj?.code}
                    loggedByName={caseObj?.production_materials_logged_by_name}
                    loggedAt={caseObj?.production_materials_logged_at}
                    onClose={() => setUnlockOpen(false)}
                    onUnlocked={() => { setUnlockOpen(false); onChange?.(); reload(); }}
                />
            )}
        </div>
    );
}


/* ───── Helpers + sub-components ──────────────────────────────────────── */

function categoryFromPurpose(bom_purpose) {
    if (!bom_purpose) return "other";
    if (bom_purpose.startsWith("aligner_sheet")) return "tpu_sheet";
    if (bom_purpose.startsWith("retainer")) return "retainer_sheet";
    if (bom_purpose === "resin") return "resin";
    if (bom_purpose === "ipa") return "ipa";
    if (bom_purpose === "packaging") return "packaging";
    return "other";
}


function PanelHeader({ alreadyLogged }) {
    return (
        <div className="px-5 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
                <Stack size={18} weight="duotone" className="text-primary" />
                <div>
                    <div className="font-display font-semibold text-sm">Materials used</div>
                    <div className="text-[11px] text-muted-foreground">
                        Auto-derived BOM · pick variant + batch · log actual consumption to pass QC
                    </div>
                </div>
            </div>
            {alreadyLogged ? (
                <span
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200"
                    data-testid="materials-logged-chip"
                >
                    <CheckCircle size={11} weight="fill" /> Logged
                </span>
            ) : (
                <span
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
                    data-testid="materials-unlogged-chip"
                    title="QC sign-off blocked until you fill this in"
                >
                    <Lock size={11} weight="fill" /> Required for QC
                </span>
            )}
        </div>
    );
}


function SummaryStrip({ summary, alreadyLogged }) {
    if (alreadyLogged) return null;
    const chips = [
        { k: "normal",    label: `${summary.normal} normal`,     cls: "bg-emerald-100 text-emerald-700" },
        { k: "moderate",  label: `${summary.moderate} moderate`, cls: "bg-amber-100 text-amber-700" },
        { k: "high",      label: `${summary.high} high`,         cls: "bg-rose-100 text-rose-700" },
    ];
    return (
        <div className="px-5 py-2 border-b border-border bg-secondary/30 flex items-center gap-2 flex-wrap text-[11px]" data-testid="materials-summary-strip">
            <span className="font-semibold">Status:</span>
            {chips.map((c) => (
                <span key={c.k} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${c.cls}`} data-testid={`materials-summary-${c.k}`}>
                    {c.label}
                </span>
            ))}
            <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${summary.substitutions > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
                data-testid="materials-summary-substitutions"
            >
                <ArrowsLeftRight size={10} weight="bold" /> {summary.substitutions} substitution{summary.substitutions === 1 ? "" : "s"}
            </span>
            <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${summary.additional > 0 ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}
                data-testid="materials-summary-additional"
            >
                <Plus size={10} weight="bold" /> {summary.additional} unplanned
            </span>
            <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${summary.batchesMissing > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}
                data-testid="materials-summary-batches"
            >
                <Barcode size={10} weight="bold" />
                {summary.batchesMissing > 0 ? `${summary.batchesMissing} batches missing` : "All batches logged"}
            </span>
            <span className="text-muted-foreground ml-auto tabular-nums">
                Total wastage: {summary.wastageUnits.toFixed(1)} units
            </span>
        </div>
    );
}


/* ───── MaterialRowCard ─────────────────────────────────────────────────
   One card per material row. Encapsulates the 3-level selectors, batch
   selector, qty inputs, substitution banner, wastage reason, and remove
   button (for additional rows). */

function MaterialRowCard({
    row, idx, readOnly, allMaterials, materialsByCategory, batches,
    onUpdate, onSetCategory, onSetMaterial, onSetVariant, onSetBatch, onSetBatchLotFreeText, onSetActual, onRemove,
}) {
    const cat = CAT_BY_ID[row.category] || CAT_BY_ID.other;
    const CatIcon = cat.Icon;
    const isAdditional = row.kind === "additional";
    const isSubstitution = !isAdditional && row.chosen_material_id && row.chosen_material_id !== row.material_id;
    const chosenMat = allMaterials.find((m) => m.id === row.chosen_material_id);
    const variants = chosenMat?.variants || [];
    const stockAfter = chosenMat ? Math.max(0, Number(chosenMat.current_stock || 0) - Number(row.actual_qty || 0)) : null;
    const actualNum = Number(row.actual_qty || 0);
    const expectedNum = Number(row.expected_qty || 0);
    const wastage = Math.max(0, actualNum - expectedNum);
    const sev = severityOf(actualNum, expectedNum);
    const categoryOptions = materialsByCategory[row.category] || [];

    /* Border-tone follows substitution > severity > additional > BOM */
    const cardTone = isSubstitution
        ? "border-amber-300 bg-amber-50/40"
        : sev === "high"
            ? "border-rose-300 bg-rose-50/40"
            : isAdditional
                ? "border-violet-300 bg-violet-50/30"
                : "border-border bg-card";

    return (
        <div
            className={`rounded-xl border-2 ${cardTone} p-3 sm:p-4 space-y-3`}
            data-testid={`materials-row-${idx}`}
            data-row-kind={row.kind}
            data-substitution={isSubstitution ? "1" : "0"}
        >
            {/* Row header — icon + label + status badges + remove btn (additional) */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-7 h-7 rounded-lg ${TONE_BG[cat.tone]} flex items-center justify-center shrink-0`}>
                        <CatIcon size={14} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-display font-semibold text-sm truncate flex items-center gap-1.5" data-testid={`materials-row-label-${idx}`}>
                            {row.row_label || cat.label}
                            {row.arch && row.arch !== "both" && (
                                <span className="text-[9px] uppercase tracking-wider font-bold text-muted-foreground px-1 py-0.5 rounded bg-secondary">
                                    {row.arch} arch
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                            {isAdditional
                                ? "Unplanned consumption — not in BOM"
                                : (row.material_default_sku && `BOM default: ${row.material_default_sku}`)}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {isSubstitution && (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`materials-badge-substituted-${idx}`}>
                            <ArrowsLeftRight size={9} weight="bold" /> Substituted
                        </span>
                    )}
                    {isAdditional ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200" data-testid={`materials-badge-additional-${idx}`}>
                            <Plus size={9} weight="bold" /> Additional
                        </span>
                    ) : (
                        <span className="inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200" data-testid={`materials-badge-bom-${idx}`}>
                            BOM
                        </span>
                    )}
                    {isAdditional && !readOnly && (
                        <button
                            type="button"
                            onClick={onRemove}
                            className="p-1 rounded hover:bg-rose-50 text-rose-600"
                            title="Remove row"
                            data-testid={`materials-remove-row-${idx}`}
                        >
                            <X size={13} weight="bold" />
                        </button>
                    )}
                </div>
            </div>

            {/* Substitution warning banner */}
            {isSubstitution && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 space-y-2" data-testid={`materials-substitution-banner-${idx}`}>
                    <div className="flex items-start gap-1.5">
                        <Warning size={12} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                        <div>
                            <span className="font-semibold">Substitution detected.</span>{" "}
                            BOM specified <span className="font-mono font-bold">{row.material_default_sku}</span>;
                            you selected <span className="font-mono font-bold">{chosenMat?.sku || row.chosen_material_id}</span>.
                            Admin will be notified.
                        </div>
                    </div>
                    {!readOnly ? (
                        <Select value={row.substitution_reason || ""} onValueChange={(v) => onUpdate({ substitution_reason: v })}>
                            <SelectTrigger className="h-8 text-xs w-full bg-white" data-testid={`materials-substitution-reason-${idx}`}>
                                <SelectValue placeholder="Why did you substitute?" />
                            </SelectTrigger>
                            <SelectContent>
                                {SUBSTITUTION_REASONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        row.substitution_reason && (
                            <div className="text-amber-800 italic">
                                Reason: {SUBSTITUTION_LABEL[row.substitution_reason] || row.substitution_reason}
                            </div>
                        )
                    )}
                </div>
            )}

            {/* Selectors row — Material / Variant / Batch */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr] gap-2">
                {/* Category (only editable on additional rows) */}
                <div>
                    <Label>Category</Label>
                    {isAdditional && !readOnly ? (
                        <Select value={row.category} onValueChange={onSetCategory}>
                            <SelectTrigger className="h-9 text-xs" data-testid={`materials-cat-${idx}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PRODUCTION_CATEGORIES.map((c) => (
                                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="h-9 px-3 rounded-md bg-muted/40 text-xs flex items-center font-semibold text-foreground/80">
                            {cat.label}
                        </div>
                    )}
                </div>

                {/* SKU / variant (1 cell stacked into [material picker + variant picker]) */}
                <div>
                    <Label>Material SKU</Label>
                    {!readOnly ? (
                        <Select
                            value={row.chosen_material_id || ""}
                            onValueChange={onSetMaterial}
                            disabled={categoryOptions.length === 0}
                        >
                            <SelectTrigger
                                className={`h-9 text-xs ${isSubstitution ? "border-amber-400" : ""}`}
                                data-testid={`materials-sku-${idx}`}
                            >
                                <SelectValue placeholder={categoryOptions.length === 0 ? "No materials in category" : "Pick SKU"} />
                            </SelectTrigger>
                            <SelectContent>
                                {categoryOptions.map((m) => (
                                    <SelectItem key={m.id} value={m.id}>
                                        <span className="font-mono">{m.sku}</span>
                                        <span className="text-muted-foreground ml-2">· {m.name}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="h-9 px-3 rounded-md bg-muted/40 text-xs flex items-center font-mono font-bold">
                            {chosenMat?.sku || "—"}
                        </div>
                    )}
                </div>

                <div>
                    <Label>Variant / Size</Label>
                    {!readOnly ? (
                        variants.length > 0 ? (
                            <Select value={row.variant_id || ""} onValueChange={onSetVariant}>
                                <SelectTrigger className="h-9 text-xs" data-testid={`materials-variant-${idx}`}>
                                    <SelectValue placeholder="Pick variant" />
                                </SelectTrigger>
                                <SelectContent>
                                    {variants.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {v.label}
                                            {v.description && <span className="text-muted-foreground ml-1">· {v.description}</span>}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <div className="h-9 px-3 rounded-md bg-muted/30 text-xs flex items-center text-muted-foreground italic">
                                {chosenMat ? `Per ${chosenMat.unit || "unit"} — auto` : "Pick SKU first"}
                            </div>
                        )
                    ) : (
                        <div className="h-9 px-3 rounded-md bg-muted/40 text-xs flex items-center">
                            {row.variant_label || "—"}
                        </div>
                    )}
                </div>
            </div>

            {/* Batch row */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2 items-end">
                <div>
                    <Label>Batch / Lot</Label>
                    {!readOnly ? (
                        batches.length > 0 ? (
                            <Select value={row.batch_id || ""} onValueChange={onSetBatch}>
                                <SelectTrigger className="h-9 text-xs" data-testid={`materials-batch-${idx}`}>
                                    <SelectValue placeholder="Pick batch" />
                                </SelectTrigger>
                                <SelectContent>
                                    {batches.map((b) => (
                                        <SelectItem key={b.id} value={b.id}>
                                            <span className="font-mono">{b.lot_number}</span>
                                            <span className="text-muted-foreground ml-1">· {Number(b.qty_remaining || 0).toFixed(1)} left</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                value={row.batch_lot || ""}
                                onChange={(e) => onSetBatchLotFreeText(idx, e.target.value)}
                                placeholder="LOT-2026-05-A (optional)"
                                className="h-9 text-xs font-mono"
                                data-testid={`materials-batch-freetext-${idx}`}
                            />
                        )
                    ) : (
                        <div className="h-9 px-3 rounded-md bg-muted/40 text-xs flex items-center font-mono">
                            {row.batch_lot || "—"}
                        </div>
                    )}
                </div>
                {!isAdditional ? (
                    <div className="text-[10px] text-muted-foreground self-center">
                        Lots help us recall affected cases if a batch is defective later. Optional but encouraged.
                    </div>
                ) : (
                    <div className="text-[10px] text-violet-700/80 self-center italic">
                        Flagged to admin as unplanned consumption.
                    </div>
                )}
            </div>

            {/* Quantities row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div>
                    <Label>Expected</Label>
                    <div className="h-9 px-3 rounded-md bg-muted/40 text-xs flex items-center font-semibold tabular-nums">
                        {isAdditional ? "—" : `${Number(row.expected_qty).toLocaleString("en-IN")} ${row.unit || ""}`}
                    </div>
                </div>
                <div>
                    <Label>Actual</Label>
                    {!readOnly ? (
                        <Input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.1"
                            value={row.actual_qty ?? ""}
                            onChange={(e) => onSetActual(e.target.value)}
                            className={`h-9 text-xs tabular-nums ${sev === "high" ? "border-rose-400" : sev === "moderate" ? "border-amber-400" : ""}`}
                            data-testid={`materials-actual-${idx}`}
                        />
                    ) : (
                        <div className="h-9 px-3 rounded-md bg-muted/40 text-xs flex items-center font-semibold tabular-nums">
                            {row.actual_qty != null ? `${Number(row.actual_qty).toLocaleString("en-IN")} ${row.unit || ""}` : "—"}
                        </div>
                    )}
                </div>
                <div>
                    <Label>Wastage</Label>
                    <div
                        className={`h-9 px-3 rounded-md text-xs flex items-center font-semibold tabular-nums ${
                            sev === "high" ? "bg-rose-100 text-rose-800"
                            : sev === "moderate" ? "bg-amber-100 text-amber-800"
                            : "bg-emerald-50 text-emerald-700"
                        }`}
                        data-testid={`materials-wastage-${idx}`}
                    >
                        +{wastage.toFixed(1)} {row.unit || ""}
                        {sev === "high" && <Lightning size={11} weight="fill" className="ml-1" />}
                    </div>
                </div>
                <div>
                    <Label>Stock after</Label>
                    <div className={`h-9 px-3 rounded-md text-xs flex items-center tabular-nums ${
                        stockAfter != null && chosenMat && stockAfter < Number(chosenMat.reorder_threshold || 0)
                            ? "bg-amber-50 text-amber-800 font-semibold"
                            : "bg-muted/30 text-muted-foreground"
                    }`}>
                        {stockAfter != null ? `${stockAfter.toLocaleString("en-IN")} ${chosenMat?.unit || ""}` : "—"}
                        {stockAfter != null && chosenMat && stockAfter < Number(chosenMat.reorder_threshold || 0) && (
                            <Warning size={11} weight="fill" className="ml-1 text-amber-600" />
                        )}
                    </div>
                </div>
            </div>

            {/* Wastage reason row (only when wastage > 0 on BOM rows) */}
            {!isAdditional && wastage > 0 && (
                <div className="rounded-lg bg-secondary/30 px-3 py-2">
                    <Label>Wastage reason</Label>
                    {!readOnly ? (
                        <Select value={row.wastage_reason || ""} onValueChange={(v) => onUpdate({ wastage_reason: v })}>
                            <SelectTrigger className="h-9 text-xs w-full bg-white" data-testid={`materials-wastage-reason-${idx}`}>
                                <SelectValue placeholder="Pick a reason" />
                            </SelectTrigger>
                            <SelectContent>
                                {WASTAGE_REASONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="h-9 px-3 rounded-md bg-card text-xs flex items-center">
                            {WASTAGE_LABEL[row.wastage_reason] || row.wastage_reason || "—"}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function Label({ children }) {
    return (
        <label className="text-[10px] font-bold tracking-[0.16em] uppercase text-muted-foreground block mb-0.5">
            {children}
        </label>
    );
}


/* iter97.21 — Bottom summary bar (matches user spec: 0 substitutions ·
   0 unplanned · All batches logged ✅). Lives at the foot of the card
   stack — a quick last-glance before the tech hits Submit. */
function BottomSummaryBar({ summary }) {
    return (
        <div
            className="rounded-xl border border-border bg-card px-4 py-3 flex items-center justify-between gap-2 flex-wrap"
            data-testid="materials-bottom-summary"
        >
            <div className="flex items-center gap-2 flex-wrap text-[11px]">
                <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold ${
                        summary.substitutions > 0
                            ? "bg-amber-100 text-amber-800 border border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                    data-testid="materials-bottom-substitutions"
                >
                    <ArrowsLeftRight size={11} weight="bold" />
                    {summary.substitutions} substitution{summary.substitutions === 1 ? "" : "s"}
                </span>
                <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold ${
                        summary.additional > 0
                            ? "bg-violet-100 text-violet-800 border border-violet-200"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                    data-testid="materials-bottom-additional"
                >
                    <Plus size={11} weight="bold" />
                    {summary.additional} unplanned
                </span>
                <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-semibold ${
                        summary.batchesMissing > 0
                            ? "bg-amber-100 text-amber-800 border border-amber-200"
                            : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    }`}
                    data-testid="materials-bottom-batches"
                >
                    {summary.batchesMissing > 0 ? <Warning size={11} weight="bold" /> : <CheckCircle size={11} weight="fill" />}
                    {summary.batchesMissing > 0
                        ? `${summary.batchesMissing} batch${summary.batchesMissing === 1 ? "" : "es"} missing`
                        : "All batches logged"}
                </span>
            </div>
            <div className="text-[11px] text-muted-foreground tabular-nums">
                Total wastage: <span className="font-semibold text-foreground">{summary.wastageUnits.toFixed(1)} units</span>
            </div>
        </div>
    );
}


/* iter97.21 — Admin unlock dialog. Calls PUT /admin/cases/{id}/unlock-materials
   with a required reason. Note: stock movements are NOT rolled back —
   only the case-level locked flag is cleared so the tech can re-log. */
function UnlockMaterialsDialog({ open, caseId, caseCode, loggedByName, loggedAt, onClose, onUnlocked }) {
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (!open) setReason(""); }, [open]);

    const submit = async () => {
        if (reason.trim().length < 5) {
            toast.error("Reason must be at least 5 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.put(`/admin/cases/${caseId}/unlock-materials`, { reason: reason.trim() });
            toast.success("Materials log unlocked — tech can now edit + resubmit");
            onUnlocked?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to unlock");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="unlock-materials-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LockOpen size={16} weight="duotone" /> Unlock materials log
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        {caseCode && <span className="font-mono font-semibold">{caseCode}</span>}
                        {loggedAt && (
                            <>
                                {" "}— logged on{" "}
                                <span className="font-semibold">
                                    {new Date(loggedAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                                </span>
                                {loggedByName && <> by <span className="font-semibold">{loggedByName}</span></>}.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900">
                    <div className="font-semibold mb-1 flex items-center gap-1">
                        <Warning size={11} weight="fill" /> Heads up
                    </div>
                    <ul className="list-disc list-inside leading-tight space-y-0.5">
                        <li>The tech&apos;s submitted log will be cleared — they&apos;ll need to re-enter and submit.</li>
                        <li>Stock movements are <em>not</em> rolled back — they represent real inventory already consumed. Use Adjust Stock if a correction is needed.</li>
                        <li>This unlock is recorded on the case audit trail.</li>
                    </ul>
                </div>
                <div>
                    <Label>Reason (≥ 5 chars)</Label>
                    <Textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        maxLength={500}
                        placeholder="e.g. Tech reported they entered the wrong sheet count. Allowing re-submission against the correct BOM."
                        data-testid="unlock-materials-reason"
                    />
                    <div className="text-[10px] text-muted-foreground mt-1 text-right tabular-nums">
                        {reason.length} / 500
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || reason.trim().length < 5}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="unlock-materials-confirm"
                    >
                        {busy ? "Unlocking…" : "Unlock log"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
